import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import CreateTask from "../CreateTask/CreateTask";
// import { getTasks } from "../../services/Service";
import { useLoginUserMutation, useGetTasksQuery, useGetProjectsQuery, useCreateTaskMutation, useUpdateTaskMutation, useDeleteTaskMutation } from "../../services/rtkClient";
import { logout } from "../../services/authUtils";

import Navbar from "../../components/Navbar";
import "./TaskTools.css";
import {
  Settings,
  Check,
  Filter,
  List,
  LayoutGrid,
  Plus,
  Folder,
  ClipboardList as Tasks,
  Menu,
  Download,
  Upload,
  Link,
  Search,
  ChevronDown,
  ChevronRight,
  Users,
  Calendar,
  Pencil as Edit,
  Trash2 as Trash,
  BookText as Book,
  User,
  History
} from "lucide-react";

const TaskTools = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const projectName = state?.projectName || sessionStorage.getItem('projectName') || "";
  const [activeTab, setActiveTab] = useState(state?.activeTab || "Board");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [defaultColumn, setDefaultColumn] = useState("todo");
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [compactMode, setCompactMode] = useState(false);
  const [projectsOpen, setProjectsOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [currentProjectName, setCurrentProjectName] = useState(projectName || "New Workspace");

  // Auto-open create task modal if navigating from Onboarding
  useEffect(() => {
    if (state?.openCreateTask) {
      setIsCreateModalOpen(true);
      // Clean up the history state so a page refresh doesn't reopen the modal
      const newState = { ...state };
      delete newState.openCreateTask;
      window.history.replaceState({ ...window.history.state, usr: newState }, '');
    }
  }, [state?.openCreateTask]);

  const getCurrentUserEmail = () => {
    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const user = JSON.parse(storedUser);
        return user.email || user.username || "default";
      }
    } catch (err) {
      console.error("Error getting user email:", err);
    }
    return "default";
  };

  const { data: serverProjectsData } = useGetProjectsQuery();
  const [createTaskMutation] = useCreateTaskMutation();
  const [updateTaskMutation] = useUpdateTaskMutation();
  const [deleteTaskMutation] = useDeleteTaskMutation();

  const recentProjects = useMemo(() => {
    // Handle result of transformResponse (which is already mapped)
    const serverProjects = Array.isArray(serverProjectsData) ? serverProjectsData : [];
    return serverProjects;
  }, [serverProjectsData]);

  const currentProjectId = useMemo(() => {
    // Priority: navigation state > sessionStorage > first project from API
    if (state?.projectId) {
      const id = Number(state.projectId);
      sessionStorage.setItem('projectId', String(id));
      return id;
    }
    const stored = sessionStorage.getItem('projectId');
    if (stored) return Number(stored);

    if (recentProjects.length > 0) {
      const id = Number(recentProjects[0].id || recentProjects[0].projectId);
      if (id) {
        sessionStorage.setItem('projectId', String(id));
        const name = recentProjects[0].name || recentProjects[0].projectName;
        if (name) sessionStorage.setItem('projectName', name);
      }
      return id;
    }
    return null; // Don't default to 1 if we haven't loaded projects yet
  }, [recentProjects, state?.projectId]);

  const { data: serverTasksData, isLoading: tasksLoading, refetch: refetchTasks } = useGetTasksQuery(currentProjectId, { skip: !currentProjectId });


  useEffect(() => {
    if (!state?.projectName && !sessionStorage.getItem('projectName') && recentProjects.length > 0) {
      const firstProjectName = recentProjects[0].name || recentProjects[0].projectName;
      if (firstProjectName) {
        setCurrentProjectName(firstProjectName);
        sessionStorage.setItem('projectName', firstProjectName);
      }
    }
    // Update currentProjectName when navigation state changes
    if (state?.projectName) {
      setCurrentProjectName(state.projectName);
      sessionStorage.setItem('projectName', state.projectName);
    }
    // Persist projectId to sessionStorage whenever we get real projects
    if (recentProjects.length > 0 && !sessionStorage.getItem('projectId')) {
      const id = recentProjects[0].id || recentProjects[0].projectId;
      if (id) sessionStorage.setItem('projectId', String(id));
    }
  }, [recentProjects, state?.projectName, state?.projectId]);

  const defaultTasks = {
    todo: [],
    inprogress: [],
    review: [],
    done: [],
  };

  const tasks = useMemo(() => {
    const serverTasksRaw = Array.isArray(serverTasksData) ? serverTasksData : [];
    const grouped = { todo: [], inprogress: [], review: [], done: [] };

    serverTasksRaw.forEach(t => {
      // Filter by project ID (redundant but safe if API returns all)
      const tProjectId = Number(t.projectId || t.project_id);
      if (currentProjectId && tProjectId !== currentProjectId) return;

      const col = (t.status || t.column || "todo").toLowerCase().replace(/\s/g, "");
      const normalized = {
        ...t,
        id: t.id || t.issueId || `KAN-${t.id || t.issueId}`,
        title: t.summary || t.task || t.title || "Untitled",
        task: t.summary || t.task || t.title || "Untitled",
        date: t.dueDate || t.date || (t.createdAt ? new Date(t.createdAt).toLocaleDateString() : "Feb 10, 2026"),
      };

      if (grouped[col]) grouped[col].push(normalized);
      else grouped.todo.push(normalized);
    });

    return grouped;
  }, [serverTasksData, currentProjectId]);

  const [optimisticTasks, setOptimisticTasks] = useState(null);

  useEffect(() => {
    setOptimisticTasks(null);
  }, [tasks]);

  const displayTasks = optimisticTasks || tasks;

  const [searchQuery, setSearchQuery] = useState("");
  const [editingTask, setEditingTask] = useState(null);
  const [showTimeSpentPopup, setShowTimeSpentPopup] = useState(false);
  const [pendingTaskMovement, setPendingTaskMovement] = useState(null);
  const [timeSpentInput, setTimeSpentInput] = useState("");
  const fileInputRef = useRef(null);

  const metrics = React.useMemo(() => {
    const totalProjects = recentProjects.length || 0;
    const tasksInProgress = (displayTasks.inprogress || []).length;
    const completedTasks = (displayTasks.done || []).length;
    const membersSet = new Set();
    Object.values(displayTasks).forEach((arr) => {
      (arr || []).forEach((t) => {
        if (t.assignee && t.assignee !== "Unassigned")
          membersSet.add(t.assignee);
      });
    });
    const teamMembers = Math.max(1, membersSet.size);
    return { totalProjects, tasksInProgress, completedTasks, teamMembers };
  }, [displayTasks, recentProjects]);

  // Local storage synchronization removed per user request

  useEffect(() => {
    const fetchTasksFromAPI = async () => {
      try {
        // const data = await getTasks();
        // if (data && typeof data === "object" && !Array.isArray(data)) {
        //   setTasks(data);
        // } else if (Array.isArray(data)) {
        //   const grouped = { todo: [], inprogress: [], review: [], done: [] };
        //   data.forEach((task) => {
        //     const col = (task.status || task.column || "todo")
        //       .toLowerCase()
        //       .replace(" ", "");
        //     if (grouped[col]) grouped[col].push(task);
        //     else grouped.todo.push(task);
        //   });
        //   setTasks(grouped);
        // }
      } catch (err) {
        console.error("Error fetching tasks:", err);
      }
    };
    fetchTasksFromAPI();
  }, []);


  const handleSubItemClick = (type, value) => {
    if (type === "Project") {
      setCurrentProjectName(value);
      sessionStorage.setItem('projectName', value);

      // CRITICAL: Find and sync the project ID so useGetTasksQuery refetches the correct data
      const project = recentProjects.find(p => p.name === value);
      if (project) {
        const projId = project.id || project.projectId;
        if (projId) {
          sessionStorage.setItem('projectId', String(projId));
          // Forcing a minor state update to trigger currentProjectId memo recalculation if needed
          setSearchQuery(prev => prev);
          // Note: Since currentProjectId memo depends on sessionStorage implicitly or through other triggers,
          // it might need a more direct trigger or we could navigate to refresh the state.
          window.dispatchEvent(new Event('storage')); // Trigger any storage listeners
        }
      }
    } else {
      alert(`Filtering by ${type}: ${value}`);
    }
  };

  const handleTaskClick = (taskTitle) =>
    console.log(`Clicked Task: ${taskTitle}`);
  const handleButtonClick = (btnName) =>
    console.log(`Clicked Button: ${btnName}`);

  const handleCreateTask = (columnLabel) => {
    const columnMap = {
      "To Do": "todo",
      "In Progress": "inprogress",
      "In Review": "review",
      Done: "done",
    };
    setDefaultColumn(columnMap[columnLabel] || "todo");
    setEditingTask(null);
    setIsCreateModalOpen(true);
  };

  const handleAddTaskToBoard = (newTaskData) => {
    console.log("Receiving new task data in TaskTools:", newTaskData);
    const fullData = newTaskData?.data || newTaskData;
    console.log("Extracted fullData:", fullData);

    const taskId = fullData?.issueKey || fullData?.id || `KAN-${Math.floor(Math.random() * 100000)}`;
    const rawColumn = (fullData?.status || fullData?.column || "todo").toLowerCase().replace(/\s+/g, "");
    const column = ["todo", "inprogress", "review", "done"].includes(rawColumn) ? rawColumn : "todo";

    console.log("Adding/Updating task with ID:", taskId, "to column:", column);

    // No manual state update needed anymore, RTK Query handles it through invalidatesTags: ["Task"]
    // and automatic refetch. We just close the modal.
    setIsCreateModalOpen(false);
    refetchTasks();

    setIsCreateModalOpen(false);
  };

  const handleEditTask = (taskId) => {
    let found = null;
    Object.entries(displayTasks).forEach(([col, arr]) => {
      arr.forEach((t) => {
        if (t.id === taskId) found = { ...t, column: col };
      });
    });
    if (found) {
      setDefaultColumn(found.column || "todo");
      setEditingTask({ id: taskId, data: found });
      setIsCreateModalOpen(true);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm("Delete this task?")) return;

    // Optimistic UI for delete
    let foundCol = null;
    let foundIdx = -1;
    Object.entries(displayTasks).forEach(([col, arr]) => {
      const idx = arr.findIndex(t => t.id === taskId);
      if (idx !== -1) {
        foundCol = col;
        foundIdx = idx;
      }
    });

    if (foundCol) {
      const newTasks = {
        todo: [...displayTasks.todo],
        inprogress: [...displayTasks.inprogress],
        review: [...displayTasks.review],
        done: [...displayTasks.done]
      };
      newTasks[foundCol].splice(foundIdx, 1);
      setOptimisticTasks(newTasks);
    }

    try {
      // Use the numeric ID if possible, otherwise taskId
      const numericId = parseInt(taskId.toString().replace("KAN-", ""));
      await deleteTaskMutation(isNaN(numericId) ? taskId : numericId).unwrap();

      // No manual tasks update needed, RTK Query refetches automatically
    } catch (err) {
      console.error("Delete Task Error:", err);
      alert("Failed to delete task. Please try again.");
      refetchTasks(); // Refetch to restore if sync failed
    }
  };

  const exportCSV = () => {
    const rows = [["id", "title", "date", "assignee", "column"]];
    Object.entries(displayTasks).forEach(([col, arr]) => {
      arr.forEach((t) =>
        rows.push([t.id, t.title, t.date, t.assignee || "", col]),
      );
    });
    const csv = rows
      .map((r) =>
        r.map((c) => `"${(c || "").toString().replace(/"/g, '""')}"`).join(","),
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${currentProjectName.replace(/\s+/g, "_")}_tasks.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const onImportFile = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const lines = text.split(/\r?\n/).filter(Boolean);
      if (!lines.length) return;
      const header = lines
        .shift()
        .split(",")
        .map((h) => h.replace(/"/g, "").trim());
      const parsed = lines.map((line) => {
        const cols = line.split(",").map((c) => c.replace(/^"|"$/g, "").trim());
        const obj = {};
        header.forEach((h, i) => (obj[h] = cols[i]));
        return obj;
      });
      const next = { todo: [], inprogress: [], review: [], done: [] };
      parsed.forEach((p) => {
        const col = p.column || p.status || "todo";
        next[col] = next[col] || [];
        next[col].push({
          id: p.id || `KAN-${Math.floor(Math.random() * 100000)}`,
          title: p.title || p.task || "Imported",
          date: p.date || "",
          assignee: p.assignee || "",
        });
      });
      // No manual tasks update needed, RTK Query refetches automatically
    };
    reader.readAsText(f);
    e.target.value = null;
  };

  // Filtering helper
  const filterTasks = (arr) => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return arr;
    return arr.filter(
      (t) =>
        (t.title || t.summary || t.task || "").toLowerCase().includes(q) ||
        (String(t.id) || "").toLowerCase().includes(q) ||
        (t.assignee || "").toLowerCase().includes(q),
    );
  };

  // ── Drag & Drop state and handlers ──────────────────────────────────────────
  const dragInfo = useRef(null); // { fromCol, taskIndex }

  const handleDragStart = (e, fromCol, taskIndex) => {
    try {
      e.dataTransfer.setData("text/plain", "");
    } catch (err) { }
    e.dataTransfer.effectAllowed = "move";
    dragInfo.current = { fromCol, taskIndex };
  };

  const handleDragEnd = () => {
    dragInfo.current = null;
  };

  const handleDragOver = (e, col, idx = null) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };


  const handleDrop = async (toCol, toIndex = null) => {
    console.log(`Dropped to column: ${toCol}, index: ${toIndex}`);
    if (!dragInfo.current) return;
    const { fromCol, taskIndex } = dragInfo.current;

    if (
      fromCol === toCol &&
      (toIndex === null || toIndex === taskIndex || toIndex === taskIndex + 1)
    ) {
      dragInfo.current = null;
      return;
    }

    if (toCol === "done" && fromCol !== "done") {
      setPendingTaskMovement({ fromCol, taskIndex, toCol, toIndex });
      setShowTimeSpentPopup(true);
      dragInfo.current = null;
      return;
    }

    const taskToMove = displayTasks[fromCol][taskIndex];

    // Optimistic UI
    const newTasks = {
      todo: [...displayTasks.todo],
      inprogress: [...displayTasks.inprogress],
      review: [...displayTasks.review],
      done: [...displayTasks.done]
    };
    newTasks[fromCol].splice(taskIndex, 1);
    const spliceIndex = toIndex !== null ? toIndex : newTasks[toCol].length;
    newTasks[toCol].splice(spliceIndex, 0, taskToMove);
    setOptimisticTasks(newTasks);

    try {
      // Sync with server
      const numericId = parseInt(taskToMove.id.toString().replace("KAN-", ""));
      const originalId = isNaN(numericId) ? taskToMove.id : numericId;
      const originalTask = Array.isArray(serverTasksData) 
        ? serverTasksData.find(t => (t.id || t.issueId) === originalId || `KAN-${t.id || t.issueId}` === taskToMove.id || t.id === taskToMove.id) 
        : null;

      const payload = originalTask ? { ...originalTask } : {};
      payload.issueId = originalTask ? (originalTask.id || originalTask.issueId) : originalId;
      payload.status = toCol;
      // Also supply orderIndex to override if required
      payload.orderIndex = toIndex !== null ? toIndex : (payload.orderIndex || 0);

      await updateTaskMutation(payload).unwrap();
    } catch (err) {
      console.error("Task move sync failed:", err);
      // We don't necessarily want to revert local state here to avoid jitter, 
      // but in a production app we might.
      refetchTasks(); // Refetch to restore correct state if sync failed
    }

    dragInfo.current = null;
  };

  const handleTimeSpentSubmit = async () => {
    if (!pendingTaskMovement) return;
    const { fromCol, taskIndex, toCol, toIndex } = pendingTaskMovement;
    const movedTask = displayTasks[fromCol][taskIndex];

    // relying on server sync

    try {
      const numericId = parseInt(movedTask.id.toString().replace("KAN-", ""));
      const originalId = isNaN(numericId) ? movedTask.id : numericId;
      const originalTask = Array.isArray(serverTasksData) 
        ? serverTasksData.find(t => (t.id || t.issueId) === originalId || `KAN-${t.id || t.issueId}` === movedTask.id || t.id === movedTask.id) 
        : null;

      const payload = originalTask ? { ...originalTask } : {};
      payload.issueId = originalTask ? (originalTask.id || originalTask.issueId) : originalId;
      payload.status = toCol;
      payload.timeSpent = Number(timeSpentInput) || 0;
      payload.orderIndex = toIndex !== null ? toIndex : (payload.orderIndex || 0);

      await updateTaskMutation(payload).unwrap();
    } catch (err) {
      console.error("Task move (done) sync failed:", err);
      refetchTasks();
    }

    setShowTimeSpentPopup(false);
    setPendingTaskMovement(null);
    setTimeSpentInput("");
  };

  return (
    <div className={`task-tools-wrapper ${compactMode ? "compact" : ""}`}>
      <Navbar
        onCreate={() => {
          setEditingTask(null);
          setIsCreateModalOpen(true);
        }}
      />
      <div className="task-tools-container">
        {isSidebarVisible && (
          <aside className="lp-sidebar">
            <div className="lp-sidebar-nav">
              <div
                className="lp-nav-item active"
                onClick={() => navigate()}
                style={{ cursor: "pointer" }}
              >
                <span className="lp-nav-icon">
                  <LayoutGrid size={18} />
                </span>
                Dashboard
              </div>

            </div>

            <div className="lp-nav-section-header">
              <span>New Project</span>
              <span className="lp-section-add" onClick={() => navigate('/onboarding')} style={{ cursor: 'pointer' }}>
                <Plus size={14} />
              </span>
            </div>
            <div className="lp-sidebar-nav secondary">
              <div
                className="lp-nav-item"
                onClick={() => setProjectsOpen(!projectsOpen)}
                style={{ cursor: "pointer" }}
              >
                <span className="lp-nav-icon">
                  <Settings size={18} />
                </span>
                Projects
                <span className="lp-nav-arrow">
                  {projectsOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </span>
              </div>
              {projectsOpen && (
                <div className="lp-sub-items">
                  {recentProjects.map((proj, idx) => (
                    <div
                      key={idx}
                      className="lp-sub-item"
                      onClick={() => handleSubItemClick("Project", proj.name)}
                    >
                      {proj.name}
                    </div>
                  ))}
                </div>
              )}

              <div
                className="lp-nav-item"
                onClick={() => setStatusOpen(!statusOpen)}
                style={{ cursor: "pointer" }}
              >
                <span className="lp-nav-icon">
                  <Tasks size={18} />
                </span>
                Status
                <span className="lp-nav-arrow">
                  {statusOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </span>
              </div>
              {statusOpen && (
                <div className="lp-sub-items">
                  <div
                    className="lp-sub-item"
                    onClick={() => handleSubItemClick("Status", "Completed")}
                  >
                    Completed
                  </div>
                  <div
                    className="lp-sub-item"
                    onClick={() => handleSubItemClick("Status", "In Progress")}
                  >
                    In Progress
                  </div>
                  <div
                    className="lp-sub-item"
                    onClick={() => handleSubItemClick("Status", "Hold")}
                  >
                    Hold
                  </div>
                </div>
              )}
            </div>

            <div className="lp-sidebar-footer">
              <button className="lp-invite-btn">
                <Users size={16} /> Invite People
              </button>
            </div>
          </aside>
        )}

        <main className="tt-main">
          <div className="tt-board-header">
            <div className="tt-header-top">
              <button
                className="sidebar-toggle-btn"
                onClick={() => setIsSidebarVisible(!isSidebarVisible)}
                title={isSidebarVisible ? "Hide Sidebar" : "Show Sidebar"}
              >
                <Menu size={18} />
              </button>
              <button
                className="btn-secondary"
                onClick={() => setCompactMode((s) => !s)}
                title={compactMode ? "Disable compact" : "Enable compact"}
                style={{ marginLeft: 8 }}
              >
                {compactMode ? "Compact: On" : "Compact"}
              </button>

            </div>

            <div className="tt-project-title">
              <div
                style={{
                  width: "24px",
                  height: "24px",
                  background: "linear-gradient(135deg, #0052cc, #6554c0)",
                  borderRadius: "4px",
                }}
              ></div>
              <h1>{currentProjectName}</h1>
              <div style={{ marginLeft: "auto", display: "flex", gap: "8px" }}>
                <button
                  className="btn-icon"
                  onClick={() => handleButtonClick("Share")}
                >
                  <Link size={18} />
                </button>
                <button
                  className="btn-icon"
                  onClick={() => handleButtonClick("Board Settings")}
                >
                  <Settings size={18} />
                </button>
              </div>
            </div>

            <div className="tt-tabs">
              <div
                className={`tt-tab ${activeTab === "Summary" ? "active" : ""}`}
                onClick={() =>
                  navigate("/summary", { state: { projectId: currentProjectId, projectName: currentProjectName } })
                }
              >
                Summary
              </div>
              <div
                className={`tt-tab ${activeTab === "Board" ? "active" : ""}`}
                onClick={() => setActiveTab("Board")}
              >
                Board
              </div>
              <div
                className={`tt-tab ${activeTab === "List" ? "active" : ""}`}
                onClick={() =>
                  navigate("/listpage", { state: { projectId: currentProjectId, projectName: currentProjectName } })
                }
              >
                Tasks List
              </div>
              <div
                className={`tt-tab ${activeTab === "Timeline" ? "active" : ""}`}
                onClick={() =>
                  navigate("/timeline", { state: { projectId: currentProjectId, projectName: currentProjectName } })
                }
              >
                Timeline
              </div>
              <div
                className={`tt-tab ${activeTab === "Calendar" ? "active" : ""}`}
                onClick={() =>
                  navigate("/calendar", { state: { projectId: currentProjectId, projectName: currentProjectName } })
                }
              >
                Calendar
              </div>
            </div>
          </div>

          <div className="tt-metrics">
            <div className="tt-metric-card">
              <div className="tt-metric-icon">
                <Folder size={20} />
              </div>
              <div className="tt-metric-body">
                <div className="tt-metric-label">Total Projects</div>
                <div className="tt-metric-value">{metrics.totalProjects}</div>
              </div>
            </div>

            <div className="tt-metric-card">
              <div className="tt-metric-icon">
                <Tasks size={20} />
              </div>
              <div className="tt-metric-body">
                <div className="tt-metric-label">Tasks in Progress</div>
                <div className="tt-metric-value">{metrics.tasksInProgress}</div>
              </div>
            </div>

            <div className="tt-metric-card">
              <div className="tt-metric-icon">
                <Check size={20} />
              </div>
              <div className="tt-metric-body">
                <div className="tt-metric-label">Completed Tasks</div>
                <div className="tt-metric-value">{metrics.completedTasks}</div>
              </div>
            </div>

            <div className="tt-metric-card">
              <div className="tt-metric-icon">
                <Users size={20} />
              </div>
              <div className="tt-metric-body">
                <div className="tt-metric-label">Team Members</div>
                <div className="tt-metric-value">{metrics.teamMembers}</div>
              </div>
            </div>
          </div>

          <div className="tt-board-controls">
            <div style={{ position: "relative" }}>
              <span
                style={{
                  position: "absolute",
                  left: "8px",
                  top: "6px",
                  color: "#5e6c84",
                }}
              >
                <Search size={16} />
              </span>
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                type="text"
                placeholder="Search board"
                style={{
                  padding: "6px 6px 6px 28px",
                  border: "1px solid #dfe1e6",
                  borderRadius: "3px",
                  width: "220px",
                }}
              />
            </div>

            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                gap: "12px",
                alignItems: "center",
              }}
            >
              <button
                className="tt-filter-btn"
                onClick={() => handleButtonClick("Group")}
              >
                <Filter size={14} /> Group <ChevronDown size={12} />
              </button>

              <div style={{ display: "flex", background: "#ebecf0", padding: "2px", borderRadius: "6px", gap: "2px" }}>
                <button
                  className="btn-icon"
                  style={{
                    background: activeTab === "List" ? "#fff" : "transparent",
                    padding: "6px 10px",
                    borderRadius: "4px",
                    boxShadow: activeTab === "List" ? "0 1px 3px rgba(0,0,0,0.1)" : "none"
                  }}
                  onClick={() => navigate("/listpage", { state: { tasks: displayTasks, projectName: currentProjectName } })}
                >
                  <List size={18} />
                </button>
                <button
                  className="btn-icon"
                  style={{
                    background: activeTab === "Board" ? "#fff" : "transparent",
                    padding: "6px 10px",
                    borderRadius: "4px",
                    boxShadow: activeTab === "Board" ? "0 1px 3px rgba(0,0,0,0.1)" : "none"
                  }}
                  onClick={() => setActiveTab("Board")}
                >
                  <LayoutGrid size={18} />
                </button>
              </div>

              <div style={{ width: "1px", height: "24px", background: "#dfe1e6", margin: "0 4px" }}></div>

              <button
                className="btn-icon"
                onClick={() => exportCSV()}
                title="Export CSV"
              >
                <Download size={18} />
              </button>
              <button
                className="btn-icon"
                onClick={() =>
                  fileInputRef.current && fileInputRef.current.click()
                }
                title="Import CSV"
              >
                <Upload size={18} />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={onImportFile}
                style={{ display: "none" }}
              />
            </div>
          </div>

          <div className="tt-board-scroll">
            <div className="tt-board-columns">
              {["todo", "inprogress", "review", "done"].map((colKey) => {
                const colLabel =
                  colKey === "todo"
                    ? "TO DO"
                    : colKey === "inprogress"
                      ? "IN PROGRESS"
                      : colKey === "review"
                        ? "IN REVIEW"
                        : "DONE";
                const colTasks = filterTasks(displayTasks[colKey] || []);
                return (
                  <div
                    className={`tt-column ${colKey === "done" ? "done" : ""}`}
                    key={colKey}
                  >
                    <div className="tt-column-header">
                      {colLabel}{" "}
                      <span className="tt-card-count">
                        {(displayTasks[colKey] || []).length}
                      </span>
                    </div>
                    <div
                      className={`tt-task-list`}
                      onDragEnter={(e) => e.preventDefault()}
                      onDragOver={(e) =>
                        handleDragOver(e, colKey, (displayTasks[colKey] || []).length)
                      }
                      onDrop={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleDrop(colKey, (displayTasks[colKey] || []).length);
                      }}
                    >
                      {colTasks.map((task, idx) => (
                        <div
                          key={task.id}
                          className={`tt-task-card priority-${(task.priority || "Medium").toLowerCase().replace(/\s+/g, "-")}`}
                          draggable
                          onDragStart={(e) => {
                            e.stopPropagation();
                            handleDragStart(e, colKey, idx);
                          }}
                          onDragEnd={handleDragEnd}
                          onDragOver={(e) => {
                            e.stopPropagation();
                            handleDragOver(e, colKey, idx);
                          }}
                          onDragEnter={(e) => {
                            e.preventDefault();
                          }}
                          onDrop={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleDrop(colKey, idx);
                          }}
                          onClick={() => handleTaskClick(task.title)}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                              }}
                            >
                              <span
                                className={`tt-priority-badge ${(task.priority || "Medium").toLowerCase().replace(/\s+/g, "-")}`}
                              >
                                {task.priority || "Medium"}
                              </span>
                              <div className="tt-card-title">{task.title}</div>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                gap: 8,
                                alignItems: "center",
                              }}
                            >
                              <Edit
                                size={14}
                                style={{ cursor: "pointer" }}
                                title="Edit"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditTask(task.id);
                                }}
                              />
                              <Trash
                                size={14}
                                style={{ cursor: "pointer" }}
                                title="Delete"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteTask(task.id);
                                }}
                              />
                            </div>
                          </div>
                          <div className="tt-card-meta">
                            <div className="tt-card-left">
                              <div className="tt-date">
                                <Calendar size={12} /> {task.startDate ? `${task.startDate} - ` : ''}{task.date}
                              </div>
                              <div className="tt-card-id">
                                <Book size={12} /> {task.id}
                              </div>
                              {task.timeSpent > 0 && (
                                <div className="tt-time-spent" title="Time Spent" style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#0052CC', fontSize: '11px', fontWeight: 'bold' }}>
                                  <History size={12} /> {task.timeSpent}h
                                </div>
                              )}
                            </div>
                            <div className="tt-user-avatar">
                              {task.assignee &&
                                task.assignee !== "Unassigned" ? (
                                String(task.assignee).charAt(0)
                              ) : (
                                <User size={14} />
                              )}
                              {task.members && task.members > 1 && (
                                <span className="tt-member-count">
                                  {task.members}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}

                      <div
                        className="create-task-btn"
                        onClick={() =>
                          handleCreateTask(
                            colKey === "todo"
                              ? "To Do"
                              : colKey === "inprogress"
                                ? "In Progress"
                                : colKey === "review"
                                  ? "In Review"
                                  : "Done",
                          )
                        }
                      >
                        + Create
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </main>

        {
          isCreateModalOpen && (
            <CreateTask
              onClose={() => {
                setIsCreateModalOpen(false);
                setEditingTask(null);
              }}
              onAddTask={handleAddTaskToBoard}
              initialColumn={defaultColumn}
              initialData={editingTask?.data}
              currentProjectId={currentProjectId}
            />
          )
        }

        {showTimeSpentPopup && (
          <div className="create-task-overlay">
            <div className="create-task-modal" style={{ maxWidth: '400px', height: 'auto' }}>
              <div className="create-task-header">
                <h2>Log Time Spent</h2>
                <div className="header-actions">
                  <button className="icon-btn" onClick={() => { setShowTimeSpentPopup(false); setPendingTaskMovement(null); }}><ChevronRight size={18} /></button>
                </div>
              </div>
              <div className="create-task-content" style={{ padding: '20px' }}>
                <div className="form-group">
                  <label className="form-label">How many hours did you spend on this task?</label>
                  <div className="input-with-unit">
                    <input
                      type="number"
                      className="form-input"
                      value={timeSpentInput}
                      onChange={(e) => setTimeSpentInput(e.target.value)}
                      autoFocus
                    />
                    <span className="input-unit">h</span>
                  </div>
                </div>
              </div>
              <div className="create-task-footer">
                <button className="btn btn-link" onClick={() => { setShowTimeSpentPopup(false); setPendingTaskMovement(null); }}>Cancel</button>
                <button className="btn btn-primary" onClick={handleTimeSpentSubmit}>Complete Task</button>
              </div>
            </div>
          </div>
        )}
      </div >
    </div >
  );
};

export default TaskTools;
